import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listIntegrations,
  getIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  type Integration,
} from "../api/config";

function IntegrationEditor({
  integration,
  onClose,
  onSaved,
}: {
  integration: Integration | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(integration?.name ?? "");
  const [type, setType] = useState(integration?.type ?? "");
  const [description, setDescription] = useState(integration?.description ?? "");
  const [config, setConfig] = useState("");

  // The list payload doesn't include config — fetch the full record on edit.
  const { isLoading: loadingDetail } = useQuery({
    queryKey: ["integration", integration?.integration_id],
    queryFn: async () => {
      const detail = await getIntegration(integration!.integration_id);
      setConfig(detail.config ?? "");
      return detail;
    },
    enabled: !!integration,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        type,
        description: description || null,
        config: config || null,
      };
      return integration
        ? updateIntegration(integration.integration_id, body)
        : createIntegration(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-bold">{integration ? "Edit" : "New"} integration</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Config (raw JSON){integration && loadingDetail && <span className="text-gray-400"> — loading…</span>}
          </label>
          <textarea
            rows={5}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            value={config}
            onChange={(e) => setConfig(e.target.value)}
          />
        </div>
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || !type || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState<Integration | "new" | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["integrations"],
    queryFn: listIntegrations,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteIntegration(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });

  const integrations = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Integrations</h1>
        <button
          onClick={() => setEditor("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New integration
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {integrations.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-gray-500">
                    No integrations configured.
                  </td>
                </tr>
              ) : (
                integrations.map((i) => (
                  <tr key={i.integration_id}>
                    <td className="px-4 py-2 font-medium">{i.name}</td>
                    <td className="px-4 py-2 uppercase text-gray-600">{i.type}</td>
                    <td className="px-4 py-2 text-gray-600">{i.description || "—"}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button onClick={() => setEditor(i)} className="text-brand hover:underline">
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete integration "${i.name}"?`)) remove.mutate(i.integration_id);
                        }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <IntegrationEditor
          integration={editor === "new" ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["integrations"] });
          }}
        />
      )}
    </div>
  );
}
